import { useTranslation } from 'react-i18next';
import {
  Badge,
  BadgeColors,
  Button,
  ButtonVariants,
  Icon,
  IconNames,
  ToggleSwitch,
} from '@triumph/design-system';
import { useAppContext } from '@/app/components/providers/AppContextProvider';
import { DestinationRow, SchedulePreferences } from '../communicationPreferences.types';
import {
  activeDestinationCount,
  dayLabel,
  formatHour,
  timeZoneLabel,
} from '../communicationPreferences.utils';
import {
  CardBody,
  CardFooter,
  CardHeader,
  CardHeaderBody,
  CardSubtitle,
  CardTitle,
  CardTitleRow,
  EffectNote,
  IconTile,
  InfoBanner,
  PreferenceCard,
  ToggleControl,
  ToggleLabel,
} from '../CommunicationPreferences.StyledComponents';
import { DeliveryDestinations } from './DeliveryDestinations';
import { DeliverySchedule } from './DeliverySchedule';

interface BillingRemindersCardProps {
  schedule: SchedulePreferences;
  rows: DestinationRow[];
  isSaving: boolean;
  showValidation: boolean;
  onToggle: () => void;
  onDayChange: (day: number) => void;
  onHourChange: (hour: number) => void;
  onTimeZoneChange: (ianaId: string) => void;
  onChangeEmail: (key: string, email: string) => void;
  onRemoveDestination: (key: string) => void;
  onAddDestination: () => void;
  onSave: () => void;
}

export const BillingRemindersCard: React.FC<BillingRemindersCardProps> = ({
  schedule,
  rows,
  isSaving,
  showValidation,
  onToggle,
  onDayChange,
  onHourChange,
  onTimeZoneChange,
  onChangeEmail,
  onRemoveDestination,
  onAddDestination,
  onSave,
}) => {
  const { t } = useTranslation('communicationPreferences');
  const { theme } = useAppContext();

  const activeCount = activeDestinationCount(rows);
  const scheduleText = t('banner.schedule', {
    day: dayLabel(schedule.dayOfWeek),
    time: formatHour(schedule.hourLocal),
    zone: timeZoneLabel(schedule.timeZone),
  });
  const hasDestinations = activeCount > 0;
  const bannerText = hasDestinations
    ? `${scheduleText}${t('banner.destinationSuffix', { count: activeCount })}`
    : t('banner.noDestinations');

  return (
    <PreferenceCard data-testid="comm-pref-billing-reminders">
      <CardHeader>
        <IconTile>
          <Icon
            name={IconNames.Alerts_NotificationBell}
            id="comm-pref-bell-icon"
            size="md"
            color={theme.colors.greyscale.gray50}
          />
        </IconTile>
        <CardHeaderBody>
          <CardTitleRow>
            <CardTitle>{t('billingReminders.title')}</CardTitle>
            <Badge
              label={
                schedule.isEnabled
                  ? t('billingReminders.activeBadge')
                  : t('billingReminders.offBadge')
              }
              color={schedule.isEnabled ? BadgeColors.SUCCESS : BadgeColors.SECONDARY}
              data-testid="comm-pref-status-badge"
            />
          </CardTitleRow>
          <CardSubtitle>{t('billingReminders.subtitle')}</CardSubtitle>
        </CardHeaderBody>
        <ToggleControl>
          <ToggleLabel data-testid="comm-pref-toggle-label">
            {schedule.isEnabled ? t('billingReminders.on') : t('billingReminders.off')}
          </ToggleLabel>
          <ToggleSwitch
            checked={schedule.isEnabled}
            onChange={onToggle}
            switchToggleContainerTestId="comm-pref-toggle"
          />
        </ToggleControl>
      </CardHeader>

      {schedule.isEnabled && (
        <InfoBanner
          variant={hasDestinations ? 'info' : 'warning'}
          data-testid="comm-pref-banner"
        >
          <Icon
            name={IconNames.Alerts_Information}
            id="comm-pref-banner-icon"
            size="sm"
            color={theme.colors.primary.actionDefault}
          />
          <span>{bannerText}</span>
        </InfoBanner>
      )}

      <CardBody>
        <DeliveryDestinations
          rows={rows}
          showValidation={showValidation}
          onChangeEmail={onChangeEmail}
          onRemove={onRemoveDestination}
          onAdd={onAddDestination}
        />
        <DeliverySchedule
          dayOfWeek={schedule.dayOfWeek}
          hourLocal={schedule.hourLocal}
          timeZone={schedule.timeZone}
          onDayChange={onDayChange}
          onHourChange={onHourChange}
          onTimeZoneChange={onTimeZoneChange}
        />
        <CardFooter>
          <EffectNote>{t('actions.effectNote')}</EffectNote>
          <Button
            variant={ButtonVariants.primary}
            onClick={onSave}
            loading={isSaving}
            disabled={isSaving}
            data-testid="comm-pref-save"
          >
            {t('actions.save')}
          </Button>
        </CardFooter>
      </CardBody>
    </PreferenceCard>
  );
};
